/**
 * screens/ProvidersScreen.tsx
 * Setup · Provider & Pipeline (softknack) — default + per-org overrides.
 * Recreated verbatim from the design HTML (~482-556), max-width 860.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, Pressable } from 'react-native';
import { useTheme } from '../context/themeContext';
import Icon from '../components/icon';
import Popover from '../components/popover';
import MenuItem from '../components/menuItem';
import { Chip, Button, SectionLabel, ScreenBody } from '../components/voiceUi';
import { PIPE_DEFAULT } from '../data/voiceMock';
// Canonical provider registry — dropdowns show labels, config stores slugs.
import {
  STT_LABELS, LLM_LABELS, TTS_LABELS, UNIFIED_LABELS,
  providerLabel, providerSlug,
} from '../data/providerRegistry';
import { getProviderConfig, listAdminOrgs, saveDefault, saveOverride, resetOverride, AdminOrg } from '../services/voice/voiceService';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

type Pipe = typeof PIPE_DEFAULT;

// estCost — UNIFIED flat 8.4, else per-LLM (keyed by canonical slug).
function estCost(cfg: Pipe): string {
  const base = cfg.mode === 'UNIFIED'
    ? 8.4
    : (({ groq: 5.2, openai: 5.9 } as Record<string, number>)[cfg.llm] || 5.9);
  return '₹' + base.toFixed(1) + ' / min';
}

// A real dropdown — the box looks identical to the HTML select, but clicking it
// opens a Popover list you pick from (replaces the old cycle-on-click behavior,
// which was unusable once the org list became real + long).
function Select({ value, options, onChange }: { value: string; options: string[]; onChange: (v: string) => void }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const box = (
    <Pressable
      style={{ height: 36, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, backgroundColor: c.card, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
      <Text style={{ fontSize: 13, color: c.ink, fontFamily: theme.font.sans }} numberOfLines={1}>{value}</Text>
      <Icon name="chev-d" size={14} color={c.ink3} />
    </Pressable>
  );
  return (
    <Popover trigger={box} width={260}>
      {(close) => (
        <View>
          {options.map((opt) => (
            <MenuItem key={opt} checked={opt === value} onPress={() => { onChange(opt); close(); }}>{opt}</MenuItem>
          ))}
        </View>
      )}
    </Popover>
  );
}

// Provider dropdown: displays human LABELS from the registry, but its value +
// onChange speak canonical SLUGS (what the config stores + the runtime matches).
function ProviderSelect({ kind, slug, labels, onChange }:
  { kind: 'stt' | 'llm' | 'tts' | 'unified'; slug: string; labels: string[]; onChange: (slug: string) => void }) {
  return (
    <Select
      value={providerLabel(kind, slug)}
      options={labels}
      onChange={(label) => onChange(providerSlug(kind, label))}
    />
  );
}

export default function ProvidersScreen() {
  const { theme } = useTheme();
  const c = theme.colors;

  // Selected target: 'default' (platform default) or a real org id. Orgs are the
  // SSOT platform list (not mock). Config is fetched PER selection from the
  // backend (one-org-at-a-time via ?orgId=), mirroring the resolve model.
  const [providerSel, setProviderSel] = useState('default');
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [pipeDefault, setPipeDefault] = useState<Pipe>({ ...PIPE_DEFAULT });
  const [override, setOverride] = useState<Pipe | null>(null); // this org's override; null = inheriting
  const [draft, setDraft] = useState<Pipe>({ ...PIPE_DEFAULT }); // the editable working copy

  const isDefault = providerSel === 'default';

  // Load the org list once (for the picker).
  useEffect(() => { void (async () => {
    const { data } = await listAdminOrgs();
    setOrgs(data);
  })(); }, []);

  // (Re)load the selected target's config whenever selection changes.
  const load = useCallback(async () => {
    try {
      const { data } = await getProviderConfig(isDefault ? undefined : providerSel);
      setPipeDefault(data.default);
      setOverride(data.override);
      // Draft = what's editable: the default itself, or (for an org) its override
      // if present else the inherited default as a starting point.
      setDraft(isDefault ? data.default : (data.override ?? data.default));
    } catch (err) {
      handleApiError(err, 'Could not load provider configuration');
    }
  }, [isDefault, providerSel]);
  // Plain effect — the console shell has no React Navigation (no useFocusEffect).
  useEffect(() => { void load(); }, [load]);

  const orgName = (id: string) => orgs.find(o => o.id === id)?.name ?? id;
  // Disambiguate duplicate org names (real data has repeats) by appending a short
  // id suffix, so the label→key mapping in the picker stays 1:1.
  const nameCount: Record<string, number> = {};
  orgs.forEach(o => { nameCount[o.name] = (nameCount[o.name] ?? 0) + 1; });
  const provOpts = [
    { key: 'default', label: 'Default configuration' },
    ...orgs.map(o => ({ key: o.id, label: nameCount[o.name] > 1 ? `${o.name} · ${o.id.slice(-4)}` : o.name })),
  ];
  const provLabels = provOpts.map(o => o.label);
  const selLabel = provOpts.find(o => o.key === providerSel)?.label ?? 'Default configuration';

  const orgHasOverride = !isDefault && !!override;
  const inheriting = !isDefault && !override;
  const editable = isDefault || orgHasOverride;

  const eff: Pipe = editable ? draft : pipeDefault;
  const defVer = pipeDefault.ver;
  const ver = isDefault ? defVer : (orgHasOverride ? (override as Pipe).ver : defVer);
  const verLabel = isDefault ? 'Default v' + defVer : (orgHasOverride ? 'Override v' + (override as Pipe).ver : 'Inherited · default v' + defVer);
  const label = isDefault ? 'Default configuration' : orgName(providerSel);
  const subtitle = isDefault ? 'Applied to every org unless overridden' : (orgHasOverride ? 'Custom override for this org' : 'Inheriting the platform default');

  // Write a field on the editable draft.
  const patch = (p: Partial<Pipe>) => setDraft(v => ({ ...v, ...p }));

  // Create a per-org override seeded from the current default → service, then reload.
  const overrideOrg = async () => {
    try {
      const resp = await saveOverride(providerSel, { ...pipeDefault });
      showSuccess(resp, 'Override created');
      await load();
    } catch (err) {
      handleApiError(err, 'Could not create override');
    }
  };
  // Drop this org's override back to inheriting the default → service, then reload.
  const resetOrg = async () => {
    try {
      const resp = await resetOverride(providerSel);
      showSuccess(resp, 'Reset to default');
      await load();
    } catch (err) {
      handleApiError(err, 'Could not reset override');
    }
  };
  // Persist the editable draft (default vs. this org's override); backend cuts the new version.
  const savePipe = async () => {
    try {
      const resp = isDefault ? await saveDefault(draft) : await saveOverride(providerSel, draft);
      showSuccess(resp, 'Configuration saved');
      await load();
    } catch (err) {
      handleApiError(err, 'Could not save configuration');
    }
  };

  // Real dropdown passes the chosen label; map it back to the target key (id).
  const pickProvider = (chosenLabel: string) => {
    const opt = provOpts.find(o => o.label === chosenLabel);
    if (opt) setProviderSel(opt.key);
  };

  const upLabel = { fontSize: 11, color: c.ink4, fontWeight: '600' as const, letterSpacing: 0.55, textTransform: 'uppercase' as const, marginBottom: 8, fontFamily: theme.font.sans };
  const selectCard = { backgroundColor: c.card, borderWidth: 1, borderColor: c.line, borderRadius: 10, padding: 15, ...(theme.shadows.sm as object) };

  return (
    <ScreenBody maxWidth={860}>
      {/* Configuration-for select + version chip */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 14, marginBottom: 16 }}>
        <View style={{ flex: 1, maxWidth: 320 }}>
          <SectionLabel style={{ marginBottom: 6, letterSpacing: 0.5 }}>Configuration for</SectionLabel>
          <Select value={selLabel} options={provLabels} onChange={pickProvider} />
        </View>
        <View style={{ flex: 1 }} />
        <Chip kind="mono">{verLabel}</Chip>
      </View>

      {/* Context banner */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 14, paddingVertical: 11, borderWidth: 1, borderColor: c.line, borderRadius: 9, backgroundColor: c.paper2, marginBottom: 16 }}>
        <View style={{ flex: 1 }}>
          <Text style={{ fontWeight: '600', fontSize: 13, color: c.ink, fontFamily: theme.font.sans }}>{label}</Text>
          <Text style={{ fontSize: 11.5, color: c.ink3, fontFamily: theme.font.sans }}>{subtitle}</Text>
        </View>
        {inheriting && <Button kind="default" label="Override for this org" onPress={overrideOrg} />}
        {orgHasOverride && <Button kind="default" label="Reset to default" onPress={resetOrg} />}
      </View>

      {/* Form (dimmed + non-interactive when inheriting) */}
      <View style={{ opacity: editable ? 1 : 0.55 }} pointerEvents={editable ? 'auto' : 'none'}>
        {/* Pipeline mode */}
        <View style={{ backgroundColor: c.card, borderWidth: 1, borderColor: c.line, borderRadius: 10, padding: 18, marginBottom: 14, ...(theme.shadows.sm as object) }}>
          <Text style={{ fontSize: 12, fontWeight: '600', color: c.ink2, marginBottom: 9, fontFamily: theme.font.sans }}>Pipeline mode</Text>
          <View style={{ flexDirection: 'row', gap: 4, padding: 4, backgroundColor: c.paper3, borderRadius: 9, marginBottom: 6 }}>
            {([['COMPOSABLE', 'Composable · STT + LLM + TTS'], ['UNIFIED', 'Unified · voice-to-voice']] as const).map(([m, txt]) => {
              const active = eff.mode === m;
              return (
                <Pressable key={m} onPress={() => patch({ mode: m })} style={{ flex: 1, height: 34, borderRadius: 7, alignItems: 'center', justifyContent: 'center', backgroundColor: active ? c.ink : 'transparent' }}>
                  <Text style={{ fontSize: 12.5, fontWeight: active ? '600' : '500', color: active ? c.paper : c.ink3, fontFamily: theme.font.sans }}>{txt}</Text>
                </Pressable>
              );
            })}
          </View>
          <Text style={{ fontSize: 11.5, color: c.ink4, fontFamily: theme.font.sans }}>Unified providers must satisfy the tool-calling + prompt-injection contract, or they are not wired in.</Text>
        </View>

        {/* Provider selects */}
        {eff.mode === 'COMPOSABLE' ? (
          <View style={{ flexDirection: 'row', gap: 12, marginBottom: 14 }}>
            <View style={{ flex: 1, ...selectCard }}>
              <Text style={upLabel}>STT</Text>
              <ProviderSelect kind="stt" slug={eff.stt} labels={STT_LABELS} onChange={v => patch({ stt: v })} />
            </View>
            <View style={{ flex: 1, ...selectCard }}>
              <Text style={upLabel}>LLM</Text>
              <ProviderSelect kind="llm" slug={eff.llm} labels={LLM_LABELS} onChange={v => patch({ llm: v })} />
            </View>
            <View style={{ flex: 1, ...selectCard }}>
              <Text style={upLabel}>TTS</Text>
              <ProviderSelect kind="tts" slug={eff.tts} labels={TTS_LABELS} onChange={v => patch({ tts: v })} />
            </View>
          </View>
        ) : (
          // Unified = one voice-to-voice model does STT + reasoning + TTS in a
          // single session. There is NO separate reasoning LLM to pick.
          <View style={{ marginBottom: 14 }}>
            <View style={{ ...selectCard, marginBottom: 8 }}>
              <Text style={upLabel}>Unified provider</Text>
              <ProviderSelect kind="unified" slug={eff.unified} labels={UNIFIED_LABELS} onChange={v => patch({ unified: v })} />
            </View>
            <Text style={{ fontSize: 11.5, color: c.ink4, fontFamily: theme.font.sans }}>
              The unified model handles speech-to-text, reasoning, and text-to-speech in one real-time session — no separate LLM.
            </Text>
          </View>
        )}

        {/* Cost / version bar */}
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14, borderWidth: 1, borderColor: c.line, borderRadius: 10, backgroundColor: c.paper2, marginBottom: 14 }}>
          <View>
            <Text style={{ fontSize: 11, color: c.ink4, fontFamily: theme.font.sans }}>Cost hint</Text>
            <Text style={{ fontFamily: theme.font.mono, fontSize: 15, fontWeight: '600', color: c.ink }}>{estCost(eff)}</Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={{ fontSize: 11, color: c.ink4, fontFamily: theme.font.sans }}>Config version</Text>
            <Text style={{ fontFamily: theme.font.mono, fontSize: 15, fontWeight: '600', color: c.ink }}>v{ver}</Text>
          </View>
        </View>
      </View>

      {/* Footer */}
      {editable ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
          <Text style={{ fontSize: 11, color: c.ink4, marginRight: 'auto', fontFamily: theme.font.sans } as any}>Saving cuts a new config version.</Text>
          <Button kind="primary" label="Save configuration" onPress={savePipe} />
        </View>
      ) : (
        <View style={{ borderWidth: 1, borderStyle: 'dashed', borderColor: c.lineStrong, borderRadius: 9, padding: 14, alignItems: 'center' }}>
          <Text style={{ fontSize: 12, color: c.ink3, fontFamily: theme.font.sans }}>This org inherits the default configuration. Create an override above to customize it.</Text>
        </View>
      )}
    </ScreenBody>
  );
}
